import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
  type UIEvent,
} from 'react';

import { Spinner } from '@/components/ui';
import type { BaseEntity } from '@/types/entities';

import { GridBody } from './GridBody';
import { GridFilterRow } from './GridFilterRow';
import { GridHeaderRow } from './GridHeaderRow';
import { GridPager } from './GridPager';
import { useReportGridRowCount } from './gridRowCountContext';
import type { DataGridHandle, DataGridProps, GridColumn } from './types';
import { GRID_PAGE_COUNT, GRID_PAGE_SIZES, useGridState } from './useGridState';

/** `Width="100%" Height="100%"` aus `IwmDataGridBase.razor`. */
const GRID_STYLE = { height: '100%', width: '100%' } as const;

/** Anzeigereihenfolge der Spalten (`Columns.OrderBy(e => e.SortIndex)`). */
function initialOrder<T>(columns: GridColumn<T>[]): string[] {
  return [...columns].sort((left, right) => left.sortIndex - right.sortIndex).map((c) => c.field);
}

/**
 * Ersatz für `SfGrid` in der Ausprägung von `Client/Components/Grids/IwmDataGridBase.razor`:
 * serverseitiges Paging und Sortieren, Filterzeile, Zellauswahl und Batch-Zellbearbeitung.
 */
export function DataGrid<T extends BaseEntity>(
  props: DataGridProps<T> & { ref?: Ref<DataGridHandle<T>> },
) {
  const { columns, dataSource, view } = props;
  const state = useGridState<T>({
    view,
    columns,
    dataSource,
    onSelectedIdChange: (id) => {
      props.onSelectedIdChange(id);
    },
  });

  // Breite und Reihenfolge der Spalten gelten nur für die Sitzung (AllowResizing/AllowReordering).
  const [order, setOrder] = useState<string[]>(() => initialOrder(columns));
  const [widths, setWidths] = useState<Record<string, number>>({});
  const appliedColumns = useRef(columns);
  useEffect(() => {
    if (appliedColumns.current === columns) return;
    appliedColumns.current = columns;
    setOrder(initialOrder(columns));
    setWidths({});
  }, [columns]);

  const visibleColumns = useMemo(() => {
    const byField = new Map(columns.map((column) => [column.field, column]));
    return order
      .map((field) => byField.get(field))
      .filter((column): column is GridColumn<T> => column !== undefined && column.visible)
      .map((column) => ({ ...column, width: widths[column.field] ?? column.width }));
  }, [columns, order, widths]);

  // Die Zeilenzahl der Kopfzeile stammt aus der Datenquelle (REQ-21 §3); vor der ersten
  // Antwort steht dort noch nichts.
  useReportGridRowCount(state.loaded ? state.totalCount : null);

  /**
   * Kopf- und Inhaltsbereich sind zwei Tabellen in zwei Behältern: nur der Inhalt scrollt
   * waagerecht, der Kopf schneidet ab (`overflow: hidden`). Damit die Spalten beieinander
   * bleiben, wird der Bildlauf auf den Kopf übertragen. `SfGrid` macht dasselbe.
   */
  const headerRef = useRef<HTMLDivElement>(null);
  const syncHeaderScroll = (event: UIEvent<HTMLDivElement>) => {
    const header = headerRef.current;
    if (header !== null) header.scrollLeft = event.currentTarget.scrollLeft;
  };

  // `OnAfterRenderAsync(firstRender)` — genau einmal nach dem ersten Rendern.
  const runInit = () => {
    void props.onInit?.();
  };
  const runInitRef = useRef(runInit);
  useEffect(() => {
    runInitRef.current = runInit;
  });
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    runInitRef.current();
  }, []);

  useImperativeHandle(
    props.ref,
    () => ({
      refresh: state.refresh,
      setRowData: state.setRowData,
      getSelectedRecords: state.getSelectedRecords,
      clearFiltering: state.clearFiltering,
      filterByColumn: state.filterByColumn,
      setColumnWhere: state.setColumnWhere,
    }),
    [state],
  );

  const colGroup = (
    <colgroup>
      {visibleColumns.map((column) => (
        <col key={column.field} style={{ width: `${String(column.width)}px` }} />
      ))}
    </colgroup>
  );

  return (
    <div id={props.id} className="e-grid e-gridhover" style={GRID_STYLE}>
      <div className="e-gridheader" ref={headerRef}>
        <div className="e-headercontent">
          <table className="e-table">
            {colGroup}
            <thead>
              <tr className="e-columnheader">
                <GridHeaderRow
                  columns={visibleColumns}
                  sorts={state.sorts}
                  onToggleSort={state.toggleSort}
                  onResize={(field, width) => {
                    setWidths((current) => ({ ...current, [field]: width }));
                  }}
                  onReorder={(field, target) => {
                    setOrder((current) => {
                      const next = current.filter((entry) => entry !== field);
                      const index = next.indexOf(target);
                      if (index < 0) return current;
                      next.splice(index, 0, field);
                      return next;
                    });
                  }}
                />
              </tr>
              <tr className="e-filterbar">
                {visibleColumns.map((column) => (
                  <th key={column.field} className="e-filterbarcell">
                    <GridFilterRow column={column} handle={state} />
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      </div>
      <div className="e-gridcontent">
        <div className="e-content e-yscroll" onScroll={syncHeaderScroll}>
          <table className="e-table">
            {colGroup}
            <tbody>
              <GridBody
                columns={visibleColumns}
                rows={state.rows}
                selected={state.selected}
                editing={state.editing}
                onSelect={state.selectCell}
                onStartEdit={state.startEdit}
                onStopEdit={state.stopEdit}
                onCellSave={(item, columnName) => {
                  void props.onCellSave(item, columnName);
                }}
              />
            </tbody>
          </table>
        </div>
      </div>
      <GridPager
        page={state.page}
        pageSize={state.pageSize}
        pageSizes={GRID_PAGE_SIZES}
        pageCount={GRID_PAGE_COUNT}
        totalCount={state.totalCount}
        onPageChange={state.setPage}
        onPageSizeChange={state.setPageSize}
      />
      {state.loading ? <Spinner /> : null}
    </div>
  );
}

export default DataGrid;
